import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { readCsrfToken, rpc, RpcError, rpcUrl } from "./rpc";
import type { FileOperation, FileOperationType, UploadInstructions } from "./types";

/**
 * The export and import surface (`docs/spec/export-import.md` §2.3). Neither operation is
 * synchronous: both return a handle the client polls until it reaches a terminal state.
 */

export const fileOperationKeys = {
  list: (type: FileOperationType) => ["fileOperations.list", type] as const,
  info: (id: string) => ["fileOperations.info", id] as const,
};

/** Terminal states. A poll stops here; nothing moves an operation out of one. */
export function isSettled(operation: FileOperation | undefined): boolean {
  return (
    operation !== undefined &&
    (operation.state === "complete" || operation.state === "error" || operation.state === "expired")
  );
}

/**
 * §2.3: poll every second, backing off to five, and give up after fifteen minutes. The
 * interval is derived from the operation's own age so that a remount does not restart it.
 */
export function pollInterval(operation: FileOperation | undefined): number | false {
  if (isSettled(operation) || operation === undefined) {
    return false;
  }
  const started = Date.parse(operation.createdAt ?? "");
  const age = Number.isNaN(started) ? 0 : Date.now() - started;
  if (age > 15 * 60 * 1000) {
    return false;
  }
  return age > 30_000 ? 5000 : 1000;
}

export function useFileOperations(type: FileOperationType): UseQueryResult<FileOperation[]> {
  return useQuery({
    queryKey: fileOperationKeys.list(type),
    queryFn: async () =>
      (await rpc<FileOperation[]>("fileOperations.list", { type, limit: 50 })).data,
    retry: false,
    // A list that holds an unfinished operation refreshes on its own, so a running export
    // reaches `complete` on screen without the reader doing anything.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((operation) => !isSettled(operation)) ? 2000 : false,
  });
}

export function useFileOperation(id: string | null): UseQueryResult<FileOperation> {
  return useQuery({
    queryKey: fileOperationKeys.info(id ?? ""),
    queryFn: async () => (await rpc<FileOperation>("fileOperations.info", { id })).data,
    enabled: id !== null,
    retry: false,
    refetchInterval: (query) => pollInterval(query.state.data),
  });
}

export interface StartExportInput {
  includeAttachments: boolean;
  includePrivate: boolean;
}

export function useStartWorkspaceExport(): UseMutationResult<
  FileOperation,
  Error,
  StartExportInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: StartExportInput) =>
      (await rpc<{ fileOperation: FileOperation }>("collections.export_all", input)).data
        .fileOperation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fileOperationKeys.list("export") });
    },
  });
}

export function useDeleteFileOperation(): UseMutationResult<void, Error, { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      await rpc("fileOperations.delete", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["fileOperations.list"] });
    },
  });
}

/** The link a completed export is downloaded from; a plain GET the browser can follow. */
export function downloadUrl(operation: FileOperation): string {
  return `${rpcUrl("fileOperations.redirect")}?id=${encodeURIComponent(operation.id)}`;
}

/**
 * Uploads a file the way every attachment is uploaded (`docs/spec/attachments.md` §3.2):
 * reserve a slot, post the signed form with the bytes, and hand back the attachment id. The
 * import endpoints take that id rather than the bytes, so a 100 MB archive never travels
 * through the JSON API.
 */
export async function uploadFile(file: File, preset: string): Promise<string> {
  const instructions = (
    await rpc<UploadInstructions>("attachments.create", {
      name: file.name,
      contentType: file.type === "" ? "application/zip" : file.type,
      size: file.size,
      preset,
    })
  ).data;

  const body = new FormData();
  for (const [key, value] of Object.entries(instructions.form ?? {})) {
    body.append(key, value);
  }
  body.append("file", file, file.name);

  const headers: Record<string, string> = {};
  const csrf = readCsrfToken();
  if (csrf !== null) {
    headers["x-csrf-token"] = csrf;
  }

  const response = await fetch(instructions.uploadUrl, {
    method: "POST",
    credentials: "include",
    headers,
    body,
  });
  if (!response.ok) {
    throw new RpcError("upload_failed", response.status, "The file could not be uploaded");
  }
  return instructions.attachment.id;
}

export interface StartImportInput {
  attachmentId: string;
  permission: "read" | "read_write" | null;
}

export function useStartWorkspaceImport(): UseMutationResult<
  FileOperation,
  Error,
  StartImportInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: StartImportInput) =>
      (await rpc<{ fileOperation: FileOperation }>("collections.import", input)).data.fileOperation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fileOperationKeys.list("import") });
      void queryClient.invalidateQueries({ queryKey: ["collections.list"] });
    },
  });
}
